import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { isEqual } from 'lodash';
import removeTypename from 'commons/removeTypename';
import getSessionKey from 'commons/getSessionKey';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import useIsRefreshCache from 'hooks/useIsRefreshCache';
import useUserData from 'data-hooks/useUserData';
import addDerivedProductData from './helpers/addDerivedProductData';

// This hook provides a reusable way to load all products for a product listing
// page, such as the category page. It first loads just initial product data
// (including filters), then when the browser is idle it loads all products.

// Uses useQuery (not useSuspenseQuery) so the query runs client-side with auth.
// This ensures trade users see correct prices on category pages (SSR would run
// without auth and return regular prices).

// To enable this functionality, it requires that the specified GraphQL query
// supports a limit variable which allows -1 to be passed in to load all.

// The session that requests are sent for, read again on every login and logout.
// The user atom only loads from localStorage after mount, which would look like
// a login on every page load.
const useSessionKey = () => {
  // React Compiler would read the cookies only once, on mount
  'use no memo';

  useUserData();
  return getSessionKey();
};
const useProductListingQuery = (query, t0) => {
  const $ = _c(44);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let otherOptions;
  let serverData;
  let t2;
  let t3;
  let t4;
  let variables;
  if ($[2] !== options) {
    ({
      variables,
      limit: t2,
      skip: t3,
      serverSidePaginationEnabled: t4,
      serverData,
      ...otherOptions
    } = options);
    $[2] = options;
    $[3] = otherOptions;
    $[4] = serverData;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = variables;
  } else {
    otherOptions = $[3];
    serverData = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    variables = $[8];
  }
  const limit = t2 === undefined ? NUMBER_OF_PRODUCTS_TO_SHOW : t2;
  const skipQuery = t3 === undefined ? false : t3;
  const serverSidePaginationEnabled = t4 === undefined ? false : t4;
  const sessionKey = useSessionKey();
  const hasLoadingAllDataStartedRef = useRef(false);
  let t5;
  if ($[9] !== variables) {
    t5 = {
      ...variables
    };
    $[9] = variables;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const prevVariables = useRef(t5);
  const prevSessionKeyRef = useRef(sessionKey);
  const initialQuerySessionKeyRef = useRef(sessionKey);
  const allDataRequestRef = useRef(0);
  const [allDataForSession, setAllDataForSession] = useState(null);
  const isRefreshCache = useIsRefreshCache();
  const isAllDataInInitialRequest = limit === -1;
  const queryName = query?.definitions?.[0]?.selectionSet?.selections?.[0]?.alias?.value ?? query?.definitions?.[0]?.name?.value;
  const skip = skipQuery || !queryName;
  const skipAllDataLoading = skip || serverSidePaginationEnabled;
  const allData = !skip && allDataForSession && allDataForSession.sessionKey === sessionKey ? allDataForSession.data : null;
  let t6;
  if ($[11] !== isRefreshCache || $[12] !== limit || $[13] !== variables) {
    t6 = {
      ...variables,
      limit,
      refreshCache: isRefreshCache
    };
    $[11] = isRefreshCache;
    $[12] = limit;
    $[13] = variables;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== otherOptions || $[16] !== skip || $[17] !== t6) {
    t7 = {
      fetchPolicy: "cache-and-network",
      variables: t6,
      skip,
      ...otherOptions
    };
    $[15] = otherOptions;
    $[16] = skip;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const initialQuery = useQuery(query, t7);
  const {
    data: initialData,
    loading,
    refetch
  } = initialQuery;
  const effectiveInitialData = initialData ?? serverData;
  const isInitialDataLoading = loading && !effectiveInitialData;
  const client = useApolloClient();
  let t8;
  let t9;
  if ($[19] !== refetch || $[20] !== sessionKey || $[21] !== skip) {
    t8 = () => {
      if (initialQuerySessionKeyRef.current === sessionKey) {
        return;
      }
      initialQuerySessionKeyRef.current = sessionKey;
      if (!skip) {
        refetch().catch(_temp);
      }
    };
    t9 = [refetch, skip, sessionKey];
    $[19] = refetch;
    $[20] = sessionKey;
    $[21] = skip;
    $[22] = t8;
    $[23] = t9;
  } else {
    t8 = $[22];
    t9 = $[23];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[24] !== client || $[25] !== isAllDataInInitialRequest || $[26] !== isRefreshCache || $[27] !== otherOptions || $[28] !== query || $[29] !== sessionKey || $[30] !== skipAllDataLoading || $[31] !== variables) {
    t10 = () => {
      if (isAllDataInInitialRequest || skipAllDataLoading) {
        return;
      }
      const hasSessionChanged = prevSessionKeyRef.current !== sessionKey;
      if (!hasLoadingAllDataStartedRef.current || !isEqual(prevVariables.current, variables) || hasSessionChanged) {
        hasLoadingAllDataStartedRef.current = true;
        prevVariables.current = {
          ...variables
        };
        prevSessionKeyRef.current = sessionKey;
        allDataRequestRef.current = allDataRequestRef.current + 1;
        const requestId = allDataRequestRef.current;
        window.requestIdleCallback(async () => {
          const {
            data: updatedAllData
          } = await client.query({
            query,
            variables: {
              ...variables,
              limit: -1,
              refreshCache: isRefreshCache
            },
            skip: skipAllDataLoading,
            ...otherOptions,
            ...(hasSessionChanged && {
              fetchPolicy: "network-only"
            })
          });
          if (requestId === allDataRequestRef.current) {
            setAllDataForSession({
              sessionKey,
              data: updatedAllData
            });
          }
        });
      }
    };
    t11 = [client, isAllDataInInitialRequest, isRefreshCache, otherOptions, query, skipAllDataLoading, sessionKey, variables];
    $[24] = client;
    $[25] = isAllDataInInitialRequest;
    $[26] = isRefreshCache;
    $[27] = otherOptions;
    $[28] = query;
    $[29] = sessionKey;
    $[30] = skipAllDataLoading;
    $[31] = variables;
    $[32] = t10;
    $[33] = t11;
  } else {
    t10 = $[32];
    t11 = $[33];
  }
  useEffect(t10, t11);
  const isAllDataLoading = !serverSidePaginationEnabled && !isAllDataInInitialRequest && !allData;
  const dataResponse = allData ?? effectiveInitialData;
  const data = dataResponse?.[queryName];
  let t12;
  if ($[34] !== data) {
    t12 = data ? removeTypename(data) : {};
    $[34] = data;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const pageData = t12;
  const {
    products: productsWithoutDerivedData,
    upholsteryOptions
  } = pageData;
  let t13;
  if ($[36] !== productsWithoutDerivedData || $[37] !== upholsteryOptions) {
    t13 = productsWithoutDerivedData?.map(product => addDerivedProductData({
      product,
      upholsteryOptions
    })) ?? [];
    $[36] = productsWithoutDerivedData;
    $[37] = upholsteryOptions;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const products = t13;
  let t14;
  if ($[39] !== isAllDataLoading || $[40] !== isInitialDataLoading || $[41] !== pageData || $[42] !== products) {
    t14 = {
      ...pageData,
      isAllDataLoading,
      isInitialDataLoading,
      products
    };
    $[39] = isAllDataLoading;
    $[40] = isInitialDataLoading;
    $[41] = pageData;
    $[42] = products;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
};
export default useProductListingQuery;
function _temp() {}